import { useEffect, useRef, useState } from "react";
import { TaskPriority } from "../graphql/generated";
import { Combobox, Popover, Portal, Transition } from "@headlessui/react";
import { getTaskPriorityText, TaskPriorityIcon } from "./TaskPriorityPill";
import { classNames } from "../utils/cn";
import { PlusIcon } from "@heroicons/react/20/solid";

export function SelectTaskPriority(props: {
  onPriorityChanged: (priority: TaskPriority) => void;
  priority?: TaskPriority;
  usePortal?: boolean;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Popover className="relative">
      {({ open }) => {
        return (
          <>
            <Popover.Button
              ref={buttonRef}
              className="relative cursor-pointer hover:border-gray-400 bg-gray-100 hover:shadow-sm flex items-center rounded-2xl border px-2 py-1"
            >
              {props.priority ? (
                <TaskPriorityIcon priority={props.priority} />
              ) : (
                <PlusIcon className="h-5 w-5 text-gray-600/80" />
              )}
              <p className="text-xs select-none text-gray-600 ml-1 truncate">
                {props.priority
                  ? getTaskPriorityText(props.priority)
                  : "Select priority"}
              </p>
            </Popover.Button>
            <SelectTaskPriorityPanel
              open={open}
              usePortal={props.usePortal}
              buttonRef={buttonRef}
              onPriorityChanged={props.onPriorityChanged}
            />
          </>
        );
      }}
    </Popover>
  );
}

function SelectTaskPriorityPanel({
  open,
  usePortal,
  buttonRef,
  onPriorityChanged,
}: {
  open: boolean;
  usePortal?: boolean;
  buttonRef: React.RefObject<HTMLButtonElement>;
  onPriorityChanged: (priority: TaskPriority) => void;
}) {
  const [panelPosition, setPanelPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    if (!usePortal || !open) {
      setPanelPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      const panelWidth = 256;
      const padding = 12;
      const top = rect.bottom + 12;
      let left = rect.left - (panelWidth * 3) / 4;

      const minLeft = padding;
      const maxLeft = window.innerWidth - panelWidth - padding;
      if (left < minLeft) left = minLeft;
      if (left > maxLeft) left = Math.max(minLeft, maxLeft);

      setPanelPosition({ top, left });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, usePortal, buttonRef]);

  if (usePortal && open && !panelPosition) {
    return null;
  }

  const panelContent = (
    <Popover.Panel
      className={
        usePortal
          ? "z-50 w-64 px-4"
          : "z-20 absolute mt-5 w-64 -translate-x-3/4 transform px-4"
      }
      style={
        usePortal && panelPosition
          ? { position: "fixed", top: panelPosition.top, left: panelPosition.left }
          : undefined
      }
    >
      {({ close }) => (
        <PopOut
          onPriorityChanged={(priority) => {
            onPriorityChanged(priority);
          }}
          closePopover={close}
        />
      )}
    </Popover.Panel>
  );

  const panel = usePortal ? (
    panelContent
  ) : (
    <Transition
      enter="ease-in-out duration-50"
      enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      enterTo="opacity-100 translate-y-0 sm:scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100 translate-y-0 sm:scale-100"
      leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
    >
      {panelContent}
    </Transition>
  );

  return usePortal ? <Portal>{panel}</Portal> : panel;
}

const priorities = [
  TaskPriority.Low,
  TaskPriority.Medium,
  TaskPriority.High,
  TaskPriority.Urgent,
];

const PopOut = ({
  onPriorityChanged,
  closePopover,
}: {
  onPriorityChanged: (priority: TaskPriority) => void;
  closePopover: () => void;
}) => {
  // Adjust the style below based on the exact positioning you need

  return (
    <div className="border rounded-md ml-10">
      <Combobox
        onChange={(f: TaskPriority) => {
          onPriorityChanged(f);
          closePopover();
        }}
      >
        <div className="">
          <div className="relative w-full cursor-default overflow-hidden rounded-t-md bg-white text-left border-b border-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
            <Combobox.Input
              placeholder="Search priorities..."
              autoFocus
              className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
              onChange={() => { }}
              value=""
            />
          </div>
          <div className="bg-white rounded-b-md">
            <Combobox.Options
              static
              className="max-h-80 scroll-py-2 divide-y  divide-gray-100 overflow-y-auto"
            >
              {priorities.map((p) => {
                return (
                  <Combobox.Option
                    key={p}
                    value={p}
                    className={({ active }) =>
                      classNames(
                        "cursor-pointer select-none last:rounded-b-md px-3 py-2",
                        active ? "bg-gray-200/70" : "",
                      )
                    }
                  >
                    {({ active }) => (
                      <div onClick={(e) => e.stopPropagation()} className="">
                        <div className="flex items-center gap-x-2">
                          <TaskPriorityIcon priority={p} />
                          <p className=" font-bold leading-tight  text-sm text-left">
                            {getTaskPriorityText(p)}
                          </p>
                        </div>
                      </div>
                    )}
                  </Combobox.Option>
                );
              })}
            </Combobox.Options>
          </div>
        </div>
      </Combobox>
    </div>
  );
};
